'use client';
import React, { useEffect, useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AppDownloadModal from '@/components/AppDownloadModal';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image: string | null;
  cover_image_alt: string | null;
  category: string;
  tags: string[];
  author_name: string | null;
  published_at: string | null;
  read_time_minutes: number;
  views: number;
}

const CATEGORIES = ['All', 'For Reviewers', 'For Brands', 'Platform Updates', 'Tips & Guides', 'Industry News'];

const FALLBACK_POSTS: BlogPost[] = [
  {
    id: '1',
    title: 'How to Write a Product Review That Gets Approved Every Time',
    slug: 'how-to-write-product-review',
    excerpt: 'Learn the exact format and tips our quality team looks for when approving reviews — and how to maximise your earnings per campaign.',
    cover_image: null,
    cover_image_alt: 'Person writing a product review on a laptop',
    category: 'Tips & Guides',
    tags: ['Reviews', 'Tips', 'Earnings'],
    author_name: 'IndianReviewClub Team',
    published_at: '2026-08-15T10:00:00Z',
    read_time_minutes: 5,
    views: 1240,
  },
  {
    id: '2',
    title: 'Why Authentic Reviews Are the New Currency for D2C Brands',
    slug: 'authentic-reviews-d2c-brands',
    excerpt: 'Fake reviews are costing Indian e-commerce brands millions in lost trust. Here\'s how verified reviews are changing the game.',
    cover_image: null,
    cover_image_alt: 'D2C brand products displayed on a shelf',
    category: 'For Brands',
    tags: ['Brands', 'E-commerce', 'Trust'],
    author_name: 'Arjun Mehta',
    published_at: '2026-08-10T10:00:00Z',
    read_time_minutes: 7,
    views: 890,
  },
  {
    id: '3',
    title: 'IndianReviewClub Crosses 50,000 Verified Reviewers',
    slug: 'milestone-50000-reviewers',
    excerpt: 'We\'re thrilled to announce that our community has grown to over 50,000 verified reviewers across 200+ cities in India.',
    cover_image: null,
    cover_image_alt: 'Celebration graphic with 50000 milestone',
    category: 'Platform Updates',
    tags: ['Milestone', 'Community'],
    author_name: 'IndianReviewClub Team',
    published_at: '2026-08-01T10:00:00Z',
    read_time_minutes: 3,
    views: 2100,
  },
  {
    id: '4',
    title: 'Top 10 Product Categories Paying the Most in August 2026',
    slug: 'top-paying-categories-august-2026',
    excerpt: 'Electronics, skincare, and fitness equipment are leading the charts. Here\'s where to focus your review efforts this month.',
    cover_image: null,
    cover_image_alt: 'Various product categories including electronics and skincare',
    category: 'For Reviewers',
    tags: ['Earnings', 'Categories', 'Monthly'],
    author_name: 'Priya Sharma',
    published_at: '2026-07-28T10:00:00Z',
    read_time_minutes: 4,
    views: 3400,
  },
  {
    id: '5',
    title: 'How Brands Can Get 10x More Value from Review Campaigns',
    slug: 'brands-10x-review-campaigns',
    excerpt: 'A step-by-step guide to setting up campaigns that attract quality reviewers, generate authentic content, and boost your product rankings.',
    cover_image: null,
    cover_image_alt: 'Brand manager reviewing campaign analytics on a screen',
    category: 'For Brands',
    tags: ['Brands', 'Campaigns', 'Strategy'],
    author_name: 'Rahul Verma',
    published_at: '2026-07-20T10:00:00Z',
    read_time_minutes: 8,
    views: 670,
  },
  {
    id: '6',
    title: 'Understanding TDS on Reviewer Earnings: A Simple Guide',
    slug: 'tds-reviewer-earnings-guide',
    excerpt: 'Confused about tax deductions on your review earnings? We break down TDS rules, Form 16A, and what you need to file your returns.',
    cover_image: null,
    cover_image_alt: 'Tax documents and calculator on a desk',
    category: 'Tips & Guides',
    tags: ['Tax', 'TDS', 'Finance'],
    author_name: 'IndianReviewClub Team',
    published_at: '2026-07-15T10:00:00Z',
    read_time_minutes: 6,
    views: 4200,
  },
];

function formatDate(dateStr: string | null) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
}

function PostCard({ post }: { post: BlogPost }) {
  const categoryColors: Record<string, string> = {
    'For Reviewers': 'bg-orange-50 text-orange-600',
    'For Brands': 'bg-blue-50 text-blue-600',
    'Platform Updates': 'bg-green-50 text-green-600',
    'Tips & Guides': 'bg-purple-50 text-purple-600',
    'Industry News': 'bg-yellow-50 text-yellow-700',
    'General': 'bg-gray-100 text-gray-600',
  };
  const colorClass = categoryColors[post.category] || 'bg-gray-100 text-gray-600';

  return (
    <Link href={`/blog/${post.slug}`} className="group block bg-white border border-gray-100 rounded-2xl overflow-hidden hover:shadow-lg transition-all hover:-translate-y-0.5">
      {/* Cover */}
      <div className="aspect-[16/9] bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        {post.cover_image ? (
          <img src={post.cover_image} alt={post.cover_image_alt || post.title} className="w-full h-full object-cover" />
        ) : (
          <Icon name="NewspaperIcon" size={40} className="text-orange-200" />
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colorClass}`}>{post.category}</span>
        </div>
        <h3 className="font-heading font-bold text-gray-900 text-base leading-snug mb-2 group-hover:text-primary transition-colors line-clamp-2">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="text-sm text-gray-500 leading-relaxed mb-4 line-clamp-2">{post.excerpt}</p>
        )}
        <div className="flex items-center justify-between text-xs text-gray-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Icon name="UserCircleIcon" size={13} />
              {post.author_name || 'Team'}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="ClockIcon" size={13} />
              {post.read_time_minutes} min read
            </span>
          </div>
          <span>{formatDate(post.published_at)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function BlogPage() {
  const [showModal, setShowModal] = useState(false);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    async function load() {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('blog_posts')
          .select('id,title,slug,excerpt,cover_image,cover_image_alt,category,tags,author_name,published_at,read_time_minutes,views')
          .eq('status', 'published')
          .order('published_at', { ascending: false });
        if (data && data.length > 0) {
          setPosts(data);
        } else {
          setPosts(FALLBACK_POSTS);
        }
      } catch {
        setPosts(FALLBACK_POSTS);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = activeCategory === 'All' ? posts : posts.filter((p) => p.category === activeCategory);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <Header onDownloadClick={() => setShowModal(true)} />
      <main className="min-h-screen bg-white pt-16">
        {/* Hero */}
        <section className="relative overflow-hidden bg-white border-b border-gray-100 py-14 px-4">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-50 rounded-full blur-3xl opacity-60 translate-x-1/2 -translate-y-1/2" />
          </div>
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 text-orange-600 text-xs font-semibold px-3.5 py-1.5 rounded-full mb-5">
              <Icon name="NewspaperIcon" size={14} />
              Blog
            </div>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl md:text-5xl text-gray-900 mb-4 leading-tight">
              Insights, guides &amp; <span className="text-primary">platform updates</span>
            </h1>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Tips for reviewers, strategies for brands, and the latest from IndianReviewClub.
            </p>
          </div>
        </section>

        {/* Category filter */}
        <div className="border-b border-gray-100 bg-white sticky top-16 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`shrink-0 text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${
                    activeCategory === cat
                      ? 'bg-primary text-white' :'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-2xl h-72 animate-pulse" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Icon name="NewspaperIcon" size={40} className="text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400">No posts in this category yet.</p>
            </div>
          ) : (
            <>
              {/* Featured post */}
              {featured && (
                <Link href={`/blog/${featured.slug}`} className="group block mb-10 bg-white border border-gray-100 rounded-3xl overflow-hidden hover:shadow-xl transition-all">
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    <div className="aspect-[4/3] lg:aspect-auto bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center min-h-[220px]">
                      {featured.cover_image ? (
                        <img src={featured.cover_image} alt={featured.cover_image_alt || featured.title} className="w-full h-full object-cover" />
                      ) : (
                        <Icon name="NewspaperIcon" size={60} className="text-orange-200" />
                      )}
                    </div>
                    <div className="p-8 flex flex-col justify-center">
                      <div className="flex items-center gap-2 mb-4">
                        <span className="text-xs font-bold text-primary uppercase tracking-widest bg-orange-50 px-3 py-1 rounded-full">Featured</span>
                        <span className="text-xs text-gray-400">{featured.category}</span>
                      </div>
                      <h2 className="font-heading font-bold text-2xl text-gray-900 mb-3 leading-snug group-hover:text-primary transition-colors">
                        {featured.title}
                      </h2>
                      {featured.excerpt && (
                        <p className="text-gray-500 text-sm leading-relaxed mb-5 line-clamp-3">{featured.excerpt}</p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1"><Icon name="UserCircleIcon" size={13} />{featured.author_name || 'Team'}</span>
                        <span className="flex items-center gap-1"><Icon name="ClockIcon" size={13} />{featured.read_time_minutes} min read</span>
                        <span>{formatDate(featured.published_at)}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Rest of posts */}
              {rest.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {rest.map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
      {showModal && <AppDownloadModal onClose={() => setShowModal(false)} />}
    </>
  );
}
